import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
  Dropdown,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { getUnpaidMonthlySubscriptions } from "../../../api/statistics/statisticsRequest";
import {
  CLIENT_TYPES,
  formatAmountInFrench,
  statusTextSubscription,
  getClientCivilityLabel,
  getClientCinOrIce,
} from "../../../utils/constants";
import { useSelector } from "react-redux";
import { formatDate } from "../../../utils/dateUtils";

const { Content } = Layout;
const { Option } = Select;
const { Title } = Typography;

const UnpaidMonthlySubscriptions = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [showAllMode, setShowAllMode] = useState("monthly"); // "monthly", "all", "to_current_month"
  const [subscriptionsData, setSubscriptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const user = useSelector((state) => state.user.user);
  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    // Initialize workers
    workerPdfRef.current = new Worker(
      new URL("../../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const {
        status,
        blobURL,
        operation,
        filename,
        message: workerMessage,
      } = e.data;

      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          setPdfExporting(true);
          break;
        case "generating":
          console.log("Creating PDF document...");
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          setPdfExporting(false);
          break;
        case "error":
          console.error("PDF generation error:", workerMessage);
          message.error(
            `Erreur lors de la génération du PDF: ${workerMessage}`
          );
          setPdfExporting(false);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
        message.success("Export Excel terminé");
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const filterUnpaidSubscriptions = async () => {
    setLoading(true);
    try {
      const requestParams = {
        month: selectedMonth,
        year: selectedYear,
      };

      // Add show_all parameter based on mode
      if (showAllMode === "all") {
        requestParams.show_all = "all";
      } else if (showAllMode === "to_current_month") {
        requestParams.show_all = "to_current_month";
      }
      // If showAllMode is "monthly", don't add show_all parameter (default behavior)

      const data = await getUnpaidMonthlySubscriptions(requestParams);
      setSubscriptionsData(data);
      message.success("Données récupérées avec succès");
    } catch (error) {
      setSubscriptionsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const handleExcelClick = async () => {
    if (!subscriptionsData || !subscriptionsData.data) {
      message.error("Aucune donnée à exporter");
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const periodLabel = getPeriodLabel();
    
    let filename;
    if (showAllMode === "all") {
      filename = `abonnements_impayes_tous_${timestamp}.xlsx`;
    } else if (showAllMode === "to_current_month") {
      filename = `abonnements_impayes_jusqua_${currentDate.getMonth() + 1}_${currentDate.getFullYear()}_${timestamp}.xlsx`;
    } else {
      filename = `abonnements_impayes_${selectedMonth}_${selectedYear}_${timestamp}.xlsx`;
    }
    
    const baseUrl = window.location.origin;

    // Prepare data for Excel export
    const formattedData = subscriptionsData.data.map((sub) => ({
      subscription_id: sub.subscription_id,
      contract_number: sub.contract_number,
      client_name: sub.client?.name || "—",
      civility: getClientCivilityLabel(sub.client),
      client_type: CLIENT_TYPES[sub.client?.type] || sub.client?.type || "—",
      address: sub.client?.address || "—",
      cin_ice: getClientCinOrIce(sub.client),
      phone: sub.client?.phone || "—",
      vehicle_matricule: sub.vehicle?.matricule || "—",
      vehicle_country: sub.vehicle?.country || "—",
      zone: sub.zone || "—",
      subscription_type: sub.subscription_type?.name || "—",
      start_date: sub.start_date ? formatDate(sub.start_date) : "—",
      status: statusTextSubscription(sub.status),
      first_unpaid_month: sub.first_unpaid_month
        ? formatDate(sub.first_unpaid_month)
        : "—",
      last_unpaid_month: sub.last_unpaid_month
        ? formatDate(sub.last_unpaid_month)
        : "—",
      pending_echeances_count: sub.pending_months?.length || 0,
      total_amount_due: sub.total_amount_due,
    }));

    const data = [
      [
        "Abonnements Impayés",
        `Période: ${periodLabel}`,
        `Total: ${subscriptionsData.totals.subscriptions} abonnements`,
      ],
      [],
      [],
      user?.full_name || "Système",
      [],
      [
        [
          { text: "ID Abonnement", type: "int", key: "subscription_id" },
          { text: "N° Contrat", type: "string", key: "contract_number" },
          { text: "Client", type: "string", key: "client_name" },
          { text: "Civilité", type: "string", key: "civility" },
          { text: "Type Client", type: "string", key: "client_type" },
          { text: "Adresse", type: "string", key: "address" },
          { text: "CIN/ICE", type: "string", key: "cin_ice" },
          { text: "Téléphone", type: "string", key: "phone" },
          { text: "Matricule", type: "string", key: "vehicle_matricule" },
          { text: "Pays", type: "string", key: "vehicle_country" },
          { text: "Parking/Zone", type: "string", key: "zone" },
          { text: "Type Abonnement", type: "string", key: "subscription_type" },
          { text: "Date Début Contrat", type: "string", key: "start_date" },
          { text: "Statut", type: "string", key: "status" },
          {
            text: "Premier Mois Impayé",
            type: "string",
            key: "first_unpaid_month",
          },
          {
            text: "Dernier Mois Impayé",
            type: "string",
            key: "last_unpaid_month",
          },
          {
            text: "Nombre Échéances Impayées",
            type: "int",
            key: "pending_echeances_count",
          },
          { text: "Montant Total Dû", type: "int", key: "total_amount_due" },
        ],
      ],
      [formattedData],
      baseUrl,
    ];

    workerExcelRef.current.postMessage({ data, filename });
  };

  const handlePdfClick = (operation) => {
    if (!subscriptionsData || !subscriptionsData.data) {
      message.error("Aucune donnée à exporter");
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const periodLabel = getPeriodLabel();
    
    let filename;
    if (showAllMode === "all") {
      filename = `abonnements_impayes_tous_${timestamp}.pdf`;
    } else if (showAllMode === "to_current_month") {
      filename = `abonnements_impayes_jusqua_${currentDate.getMonth() + 1}_${currentDate.getFullYear()}_${timestamp}.pdf`;
    } else {
      filename = `abonnements_impayes_${selectedMonth}_${selectedYear}_${timestamp}.pdf`;
    }
    
    const baseUrl = window.location.origin;

    // Prepare data for PDF export
    const formattedData = subscriptionsData.data.map((sub) => ({
      subscription_id: sub.subscription_id,
      contract_number: sub.contract_number,
      client_name: sub.client?.name || "—",
      vehicle_matricule: sub.vehicle?.matricule || "—",
      zone: sub.zone || "—",
      subscription_type_name: sub.subscription_type?.name?.replace("Abonnement ", "") || "—",
      start_date: sub.start_date ? formatDate(sub.start_date) : "—",
      status: statusTextSubscription(sub.status),
      first_unpaid_month: sub.first_unpaid_month
        ? formatDate(sub.first_unpaid_month)
        : "—",
      last_unpaid_month: sub.last_unpaid_month
        ? formatDate(sub.last_unpaid_month)
        : "—",
      pending_count: sub.pending_months?.length || 0,
      total_amount_due: formatAmountInFrench(sub.total_amount_due),
    }));

    const data = [
      [
        "Abonnements Impayés",
        `Période: ${periodLabel}`,
        `Date de génération: ${new Date().toLocaleDateString("fr-FR")}`,
        `Généré par: ${user?.full_name || "Système"}`,
      ],
      [],
      [],
      `Total: ${subscriptionsData.totals.subscriptions} abonnements | Montant : ${subscriptionsData.totals.amount_due}`,
      [],
      [
        [
          { text: "ID", type: "string", key: "subscription_id", align: "center" },
          { text: "N° Contrat", type: "string", key: "contract_number" },
          { text: "Client", type: "string", key: "client_name" },

          { text: "Matricule", type: "string", key: "vehicle_matricule" },
          { text: "Contrat", type: "string", key: "zone" },
          { text: "Type", type: "string", key: "subscription_type_name" },
          { text: "Date Début", type: "string", key: "start_date" },
          { text: "Statut", type: "string", key: "status" },
          { text: "1er Mois Impayé", type: "string", key: "first_unpaid_month" },
          { text: "Dernier Mois Impayé", type: "string", key: "last_unpaid_month" },
          { text: "Échéances", type: "string", key: "pending_count", align: "center" },
          { text: "Montant Total Dû", type: "string", key: "total_amount_due", align: "right" },
        ],
      ],
      [formattedData],
      baseUrl,
    ];

    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const getPdfMenuItems = (handlePdfClick, pdfExporting) => [
    {
      key: "print",
      label: <span className="hover:text-red-500">Télécharger</span>,
      onClick: () => handlePdfClick("download"),
      disabled: pdfExporting,
    },
    {
      key: "open",
      label: <span className="hover:text-red-500">Ouvrir</span>,
      onClick: () => handlePdfClick("open"),
      disabled: pdfExporting,
    },
  ];

  const getMonthName = (month) => {
    const months = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];
    return months[month - 1];
  };

  const getPeriodLabel = () => {
    if (showAllMode === "all") {
      return "Tous les mois impayés";
    } else if (showAllMode === "to_current_month") {
      return `Impayés jusqu'à ${getMonthName(currentDate.getMonth() + 1)} ${currentDate.getFullYear()}`;
    } else {
      return `${getMonthName(selectedMonth)} ${selectedYear}`;
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "subscription_id",
      key: "subscription_id",
      width: 80,
      fixed: "left",
    },
    {
      title: "N° Contrat",
      dataIndex: "contract_number",
      key: "contract_number",
      width: 120,
    },
    {
      title: "Client",
      dataIndex: ["client", "name"],
      key: "client",
      width: 200,
      render: (text) => text || "—",
    },
    {
      title: "Type Client",
      dataIndex: ["client", "type"],
      key: "client_type",
      width: 120,
      render: (type) => CLIENT_TYPES[type] || type || "—",
    },
    {
      title: "Véhicule",
      key: "vehicle",
      width: 150,
      render: (_, record) => (
        <div>
          <div><strong>{record.vehicle?.matricule || "—"}</strong></div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.vehicle?.country || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Type Abonnement",
      dataIndex: ["subscription_type", "name"],
      key: "subscription_type",
      width: 180,
      render: (name) => name || "—",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span style={{ color: status === "active" ? "#52c41a" : "#faad14" }}>
          {status === "active" ? "Actif" : status}
        </span>
      ),
    },
    {
      title: "Échéances En Attente",
      dataIndex: "pending_months",
      key: "pending_months",
      width: 150,
      align: "center",
      render: (pendingMonths) => (
        <strong>{pendingMonths?.length || 0}</strong>
      ),
      sorter: (a, b) => (a.pending_months?.length || 0) - (b.pending_months?.length || 0),
    },
    {
      title: "Montant Dû ",
      dataIndex: "total_amount_due",
      key: "total_amount_due",
      width: 150,
      align: "right",
      render: (amount) => (
        <strong style={{ color: "#ff4d4f" }}>
          {formatAmountInFrench(amount)}
        </strong>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => window.open(`/subscriptions/${record.subscription_id}`, "_blank")}
        >
          Voir
        </Button>
      ),
    },
  ];

  // Generate year options (current year and 5 years back)
  const yearOptions = [];
  for (let i = 0; currentDate.getFullYear() - i >= 2025; i++) {
    yearOptions.push(currentDate.getFullYear() - i);
  }

  return (
    <Layout className="min-h-screen">
      <Content style={{ padding: "24px" }} className="py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={8} md={6}>
              <label style={{ fontWeight: 500 }}>Mois</label>
              <Select
                style={{ width: "100%" }}
                value={selectedMonth}
                onChange={setSelectedMonth}
                disabled={showAllMode !== "monthly"}
              >
                <Option value={1}>Janvier</Option>
                <Option value={2}>Février</Option>
                <Option value={3}>Mars</Option>
                <Option value={4}>Avril</Option>
                <Option value={5}>Mai</Option>
                <Option value={6}>Juin</Option>
                <Option value={7}>Juillet</Option>
                <Option value={8}>Août</Option>
                <Option value={9}>Septembre</Option>
                <Option value={10}>Octobre</Option>
                <Option value={11}>Novembre</Option>
                <Option value={12}>Décembre</Option>
              </Select>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <label style={{ fontWeight: 500 }}>Année</label>
              <Select
                style={{ width: "100%" }}
                value={selectedYear}
                onChange={setSelectedYear}
                disabled={showAllMode !== "monthly"}
              >
                {yearOptions.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <label style={{ fontWeight: 500 }}>Mode d'affichage</label>
              <Select
                style={{ width: "100%" }}
                value={showAllMode}
                onChange={setShowAllMode}
              >
                <Option value="monthly">Mois spécifique</Option>
                <Option value="to_current_month">Jusqu'au mois actuel</Option>
                <Option value="all">Tous les mois impayés</Option>
              </Select>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={filterUnpaidSubscriptions}
                loading={loading}
                style={{ marginTop: 22 }}
                block
              >
                Rechercher
              </Button>
            </Col>
          </Row>
        </Card>

        {subscriptionsData && (
          <>
            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Abonnements Impayés"
                    value={subscriptionsData.totals.subscriptions}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Échéances En Attente"
                    value={subscriptionsData.totals.pending_echeances}
                    valueStyle={{ color: "#faad14" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Montant Total Dû"
                    value={`${formatAmountInFrench(subscriptionsData.totals.amount_due)}`}
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Table */}
            <Card>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Abonnements Impayés - {getPeriodLabel()}
                  <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 8, color: "#666" }}>
                    ({subscriptionsData.data?.length || 0} résultats)
                  </span>
                </Title>
                <Space>
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={handleExcelClick}
                    className="hover:border-green-700 hover:text-green-500"
                  >
                    Export Excel
                  </Button>
                  <Dropdown
                    menu={{
                      items: getPdfMenuItems(handlePdfClick, pdfExporting),
                    }}
                    disabled={pdfExporting}
                    className="hover:border-red-700 hover:text-red-500"
                  >
                    <Button icon={<FileTextOutlined />} disabled={pdfExporting}>
                      Export PDF{" "}
                      {pdfExporting ? (
                        "..."
                      ) : (
                        <span style={{ marginLeft: 5 }}>▼</span>
                      )}
                    </Button>
                  </Dropdown>
                </Space>
              </div>
              <Table
                columns={columns}
                dataSource={subscriptionsData.data}
                rowKey="subscription_id"
                scroll={{ x: 1400 }}
                pagination={{
                  defaultPageSize: 15,
                  showSizeChanger: true,
                  pageSizeOptions: ["15", "30", "50", "100"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total} abonnements`,
                }}
                size="small"
              />
            </Card>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default UnpaidMonthlySubscriptions;
